"use client";

import { useEffect, useRef } from "react";
import { useLocale } from "next-intl";

// The two OneTrust controls on the cookie policy, driven by us rather than by
// OneTrust's own DOM scan.
//
// OneTrust binds `.ot-sdk-show-settings` and fills `#ot-sdk-cookie-policy` once,
// while its SDK initialises. GTM loads that SDK on the first interaction or
// 2.5s after load (see DeferredGoogleTagManager), and the main route to this
// page is the footer link -- a client-side navigation, so these nodes mount
// long after that init has run and OneTrust never sees them. Left to its scan,
// the settings button is inert and the cookie table stays empty for exactly the
// visitor who went looking for them.
//
// So the button carries no `ot-sdk-show-settings` class: our own handler is the
// only one, which also means a click can never toggle the panel twice.

// OneTrust arrives behind deferred GTM, so it is normal for it to be missing
// for a second or two after this mounts. Poll until it lands, then stop.
const POLL_MS = 400;
const MAX_WAIT_MS = 20_000;

function whenOneTrustReady(run: () => boolean): () => void {
  if (run()) return () => {};
  let waited = 0;
  const timer = setInterval(() => {
    waited += POLL_MS;
    if (run() || waited >= MAX_WAIT_MS) clearInterval(timer);
  }, POLL_MS);
  return () => clearInterval(timer);
}

// OneTrust picks its language when it initialises and never revisits it, so the
// language switcher can leave a Dutch cookie table on the English page. Both
// controls set it before they do anything else.
//
// Same tick, deliberately. An earlier version awaited changeLanguage before
// running the action, which bought three failure modes for a cosmetic fix: a
// synchronous throw skipped the action entirely, two clicks during the in-flight
// promise toggled the panel open and straight back shut, and two locale chains
// could settle out of order and re-render the Dutch table under English copy.
//
// ponytail: known ceiling. If OneTrust's changeLanguage turns out to do its work
// asynchronously, the table paints before the language flips and stays in the
// previous one until the next full load -- on one path only, a client-side
// locale switch on this page. The upgrade is to await it and re-render, which is
// what the three defects above came from, so do it with a real await, a single
// in-flight chain per control, and a test that switches locale mid-flight.
// Not worth that machinery until someone sees the mismatch on the live CMP.
function withLanguage(locale: string, then: () => void) {
  // Two guards, not one. A CMP that refuses the locale must not cost the
  // visitor the cookie table, so the language attempt cannot skip the action;
  // and a throw from the action itself must not reach React's error boundary
  // through a click handler or an effect body and take the page with it.
  try {
    window.OneTrust?.changeLanguage?.(locale);
  } catch {
    // Wrong language beats no cookie policy.
  }
  try {
    then();
  } catch {
    // Leaves the control inert, which is the state it was in before OneTrust
    // arrived at all.
  }
}

export function CookieSettingsButton({ label }: { label: string }) {
  const locale = useLocale();
  // The click that opens the panel can be the same click that loads OneTrust:
  // GTM starts on the first pointerdown, so someone landing straight on this
  // page hits a button whose SDK is still downloading. Waiting instead of
  // returning early is the difference between "opens a moment later" and "does
  // nothing until you click it twice".
  const cancel = useRef<(() => void) | null>(null);
  useEffect(() => () => cancel.current?.(), []);

  const openPreferences = () => {
    cancel.current?.();
    cancel.current = whenOneTrustReady(() => {
      const toggle = window.OneTrust?.ToggleInfoDisplay;
      if (!toggle) return false;
      withLanguage(locale, toggle);
      return true;
    });
  };

  return (
    <button
      id="ot-sdk-btn"
      type="button"
      onClick={openPreferences}
      className="rounded-lg border border-line px-4 py-2 text-[15px] font-semibold text-navy transition-colors hover:bg-mist"
    >
      {label}
    </button>
  );
}

export function CookieList() {
  const locale = useLocale();

  useEffect(
    () =>
      whenOneTrustReady(() => {
        const render = window.OneTrust?.initializeCookiePolicyHtml;
        if (!render) return false;
        withLanguage(locale, render);
        return true;
      }),
    [locale],
  );

  return <div id="ot-sdk-cookie-policy" />;
}
